import { useState, useCallback } from 'react';
import { reportService } from '../services/reportService';

export const useReports = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [savedReports, setSavedReports] = useState([]);

  const generateReport = useCallback(async (reportType, filters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      let reportData;
      
      // Call the appropriate API based on report type
      switch (reportType) {
        case 'profit_loss':
          reportData = await reportService.getProfitLoss(filters);
          break;
        case 'balance_sheet':
          reportData = await reportService.getBalanceSheet(filters);
          break;
        case 'cash_flow':
          reportData = await reportService.getCashFlow(filters);
          break;
        case 'trial_balance':
          reportData = await reportService.getTrialBalance(filters);
          break;
        default:
          reportData = await reportService.generateReport(reportType, filters);
      }
      
      setGeneratedReport(reportData);
      return reportData;
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err.message || 'Failed to generate report');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getAccountTransactions = useCallback(async (accountId, filters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const transactions = await reportService.getAccountTransactions(accountId, filters);
      return transactions;
    } catch (err) {
      console.error('Error getting account transactions:', err);
      setError(err.message || 'Failed to get account transactions');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSavedReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const reports = await reportService.getSavedReports();
      setSavedReports(reports);
      return reports;
    } catch (err) {
      console.error('Error loading saved reports:', err);
      setError(err.message || 'Failed to load saved reports');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSavedReport = useCallback(async (reportId) => {
    setLoading(true);
    setError(null);
    
    try {
      const reportData = await reportService.loadSavedReport(reportId);
      setGeneratedReport(reportData);
      return reportData;
    } catch (err) {
      console.error('Error loading saved report:', err);
      setError(err.message || 'Failed to load saved report');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const saveCustomReport = useCallback(async (reportData) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await reportService.saveCustomReport(reportData);
      // Refresh saved reports list
      await loadSavedReports();
      return result;
    } catch (err) {
      console.error('Error saving custom report:', err);
      setError(err.message || 'Failed to save custom report');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadSavedReports]);

  const deleteSavedReport = useCallback(async (reportId) => {
    setLoading(true);
    setError(null);
    
    try {
      await reportService.deleteSavedReport(reportId);
      // Refresh saved reports list
      await loadSavedReports();
    } catch (err) {
      console.error('Error deleting saved report:', err);
      setError(err.message || 'Failed to delete saved report');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadSavedReports]);

  const exportReport = useCallback(async (reportData, format = 'pdf') => {
    setLoading(true);
    setError(null);
    
    try {
      const blob = await reportService.exportReport(reportData, format);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportData.title || 'report'}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return blob;
    } catch (err) {
      console.error('Error exporting report:', err);
      setError(err.message || 'Failed to export report');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearReport = useCallback(() => {
    setGeneratedReport(null);
  }, []);

  return {
    loading,
    error,
    generatedReport,
    savedReports,
    generateReport,
    getAccountTransactions,
    loadSavedReports,
    loadSavedReport,
    saveCustomReport,
    deleteSavedReport,
    exportReport,
    clearError,
    clearReport,
  };
};

export default useReports;